/**
 * Admin shell — sidebar + main content area.
 *
 * Mirrors the structure of the customer SPA's ``AppShell``: fixed
 * left rail, scrollable content. Different visual treatment though
 * — this dashboard is a TOOL, so we go for the dense, neutral
 * look of Linear / Stripe rather than the customer-facing bento
 * gradients. Slate background, indigo accent, no playful chrome.
 */
import { NavLink, Outlet } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Bot,
  LayoutDashboard,
  Megaphone,
  Server,
  Settings,
  Users,
} from "lucide-react";

import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  phase: 1 | 2;
}

/**
 * Sidebar entries — Phase 1 ships Overview / Customers / AI Usage.
 * Revenue / Marketing / DevOps / Settings exist as stubs so the
 * navigation feels complete but click through to "coming soon"
 * placeholders. Removes the chrome reorganization risk when those
 * pages actually ship.
 */
const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, phase: 1 },
  { to: "/customers", label: "Customers", icon: Users, phase: 1 },
  { to: "/revenue", label: "Revenue", icon: BarChart3, phase: 2 },
  { to: "/marketing", label: "Newsletter", icon: Megaphone, phase: 1 },
  { to: "/devops", label: "DevOps", icon: Server, phase: 1 },
  { to: "/ai-usage", label: "AI Usage", icon: Bot, phase: 1 },
  { to: "/settings", label: "Settings", icon: Settings, phase: 2 },
];

export function AdminLayout() {
  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside className="w-60 shrink-0 bg-slate-900 text-slate-100 flex flex-col">
        {/* Brand — the "ADMIN" pill makes it unmistakable which
            surface the founder is in. Customer SPA has the
            colorful logo; this one is plain text + a label. */}
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-800">
          <span className="text-base font-semibold tracking-tight">
            Lenavio
          </span>
          <span className="rounded-md bg-accent/20 text-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            Admin
          </span>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm",
                  isActive
                    ? "bg-accent text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.phase === 2 && (
                <span
                  className="text-[9px] uppercase tracking-wider text-slate-500"
                  aria-label="Coming soon"
                >
                  soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer chrome — internal-only warning. The dashboard
            is for one person; the warning keeps that intent
            obvious in screenshots, support sessions, etc. */}
        <div className="border-t border-slate-800 px-4 py-3 flex items-center gap-2 text-[11px] text-slate-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Internal only.{" "}
            <a
              href="https://app.lenavio.com"
              className="underline-offset-2 hover:underline"
            >
              Customer view
            </a>
          </span>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────── */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
